// TODO refactor this to a separate components as some parts can be reused by other actions types
import React, { useState, useEffect, useContext, FC, useRef } from 'react'
import classNames from 'classnames'
import { ButtonPrimary, ButtonLight } from 'front-end/src/components'
import { Trans, t } from '@lingui/macro'
import striptags from 'striptags'

import Label from '../../../../components/common/form/label/Label'
import Input from '../../../../components/common/form/input/Input'
import IconArrowLeft from '../../../../components/common/icon/IconArrowLeft'
import IconArrowRight from '../../../../components/common/icon/IconArrowRight'
import uniqueId from '../../../../components/utils/uniqueId'
import { CampaignBanner } from '../../components/campaign-banner/CampaignBanner'
import httpApi from '../../../../api/http-api'
import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { notification } from '../../../../components/common/notifications/Notifications'
import CustomQuill from '../../../../components/common/quill/CustomQuill'
import { createBanner } from '../../campaign-action/operations/createBanner'
import { patchBanner } from '../../campaign-action/operations/patchBanner'
import { IImageVariant } from '../../../../../../types/CoverImage'
import { ManageCampaignContext } from '../../ManageCampaignPage'
import NewActionHeader from '../new-action-header/NewActionHeader'

import './CampaignBannerComponent.less'
import { CampaignBannerStepSchedule } from './CampaignBannerStepSchedule'
import { CampaignActionEditingSteps } from '../../common'
import {
  CampaignBannerInput,
  CampaignBannerModelAsEditor,
  CampaignScheduleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { PREVIEW_VARS } from 'shared/preview'
import { interpolate } from 'shared/interpolate'
import { TemplatingInfoBar } from '../TemplatingInfoBar'
import dayjs, { Dayjs } from 'dayjs'
import {
  getTimeZoneDateFromUTCDate,
  getUTCDateFromTimeZoneDate
} from '../../../manage-events/event-edit/tabs/event-detail/eventDetailUtils'

export interface ICampaignBannerComponentProps {
  banner?: CampaignBannerModelAsEditor
}

export interface ICampaignBannerComponentState {
  coverImageFile: File | null
  coverImageBase64: string | null
  coverImageSrc: string | null
  bannerHeader: string
  bannerBody: string
  buttonLink: string
  buttonText: string
  showAt: string
  hideAt: string
  step: string
  companyName: string
  scheduleType: string | null
  relativeSchedule: {
    dayFrom: number | null
    dayTo: number | null
    timeFrom: string | null
    timeTo: string | null
    valid?: boolean
  }
  fixedSchedule: {
    dateFrom: Dayjs | null
    dateTo: Dayjs | null
    valid?: boolean
  }
  isSaving: boolean
  saveError: Error | null
  isEditing: boolean
  coverImageVariants: IImageVariant[]
  timezone: string
}

export const CampaignBannerComponent: FC<ICampaignBannerComponentProps> = (
  props
) => {
  const { campaign } = useContext(ManageCampaignContext)

  const [state, setState] = useState<ICampaignBannerComponentState>({
    coverImageFile: null,
    coverImageBase64: null,
    coverImageSrc: null,
    bannerHeader: '',
    bannerBody: '',
    buttonLink: '',
    buttonText: '',
    showAt: '',
    hideAt: '',
    step: CampaignActionEditingSteps.CREATE,
    companyName: '',
    scheduleType: campaign.schedule_type as CampaignScheduleEnum,
    relativeSchedule: {
      dayFrom: null,
      dayTo: null,
      timeFrom: null,
      timeTo: null
    },
    fixedSchedule: {
      dateFrom: null,
      dateTo: null
    },
    isSaving: false,
    saveError: null,
    isEditing: false,
    coverImageVariants: [],
    timezone: ''
  })

  useEffect(() => {
    const { banner } = props

    if (banner) {
      const timezone = banner.timezone ? banner.timezone : dayjs.tz.guess()
      const imageUrl = getImagePreviewUrl(banner)
      onStateUpdate({
        bannerHeader: banner.header || '',
        bannerBody: banner.text as string,
        coverImageSrc: imageUrl,
        coverImageVariants: [],
        buttonLink: banner.button_link,
        buttonText: banner.button_caption,
        relativeSchedule: {
          // @ts-expect-error 9/11/2020
          dayFrom: banner.show_at_day,
          timeFrom: banner.show_at_time ?? null,
          // @ts-expect-error 9/11/2020
          dayTo: banner.hide_at_day,
          timeTo: banner.hide_at_time ?? null
        },
        fixedSchedule: {
          dateFrom: banner.fixed_show_time_start
            ? getTimeZoneDateFromUTCDate({
                date: banner.fixed_show_time_start,
                timezone
              })
            : null,
          dateTo: banner.fixed_show_time_end
            ? getTimeZoneDateFromUTCDate({
                date: banner.fixed_show_time_end,
                timezone
              })
            : null
        },
        isEditing: true,
        timezone: timezone
      })
    }
  }, [props])

  const onStateUpdate = (
    updatedState: Partial<ICampaignBannerComponentState>
  ) => {
    setState((prevState) => ({ ...prevState, ...updatedState }))
  }

  const [createBannerFn] = createBanner()
  const create = async (input: CampaignBannerModelAsEditor) => {
    try {
      onStateUpdate({ isSaving: true, saveError: null })
      const { data } = await createBannerFn({
        variables: {
          id: campaign.id,
          input: input as CampaignBannerInput
        }
      })
      return data?.admin.campaign.createBanner
    } catch (err) {
      onStateUpdate({ saveError: err, isSaving: false })
      return null
    }
  }

  const [patchBannerFn] = patchBanner()
  const updateBannerAndState = async (input: CampaignBannerModelAsEditor) => {
    if (campaign.id && props.banner?.id) {
      try {
        onStateUpdate({ isSaving: true, saveError: null })
        const { data } = await patchBannerFn({
          variables: {
            id: props.banner?.id,
            campaignId: campaign.id,
            input: input as CampaignBannerInput
          }
        })
        return data?.admin.campaign.banner
      } catch (err) {
        onStateUpdate({ saveError: err, isSaving: false })
        return null
      }
    }
  }

  const onSave = async () => {
    const data: any = {
      text: state.bannerBody,
      header: state.bannerHeader,
      button_caption: state.buttonText,
      button_link: state.buttonLink
    }

    if (state.scheduleType === CampaignScheduleEnum.RELATIVE) {
      data.show_at_day = state.relativeSchedule.dayFrom
      data.show_at_time = state.relativeSchedule.timeFrom
      data.hide_at_day = state.relativeSchedule.dayTo
      data.hide_at_time = state.relativeSchedule.timeTo
      data.fixed_show_time_start = data.fixed_show_time_end = null
    } else {
      const timezone = state.timezone || dayjs.tz.guess()
      data.timezone = timezone
      data.fixed_show_time_start = getUTCDateFromTimeZoneDate({
        date: state.fixedSchedule.dateFrom,
        timezone
      })
      data.fixed_show_time_end = getUTCDateFromTimeZoneDate({
        date: state.fixedSchedule.dateTo,
        timezone
      })
      data.show_at_day = data.hide_at_day = null
    }

    const isCreating = state.isEditing === false

    const savedBanner = isCreating
      ? await create(data)
      : await updateBannerAndState(data)
    if (!savedBanner) {
      state.isSaving = false
      notification.error(
        isCreating ? t`Error creating banner` : t`Error updating banner`
      )
      return
    }

    if (state.coverImageFile) {
      try {
        await httpApi.campaign.uploadCampaignBannerImage(
          campaign.id,
          savedBanner.id,
          state.coverImageFile
        )
      } catch (err) {
        console.error(err)
        notification.error(t`Error uploading image`)
      }
    }

    state.isSaving = false
    notification.info(
      isCreating
        ? t`Campaign's banner has been created`
        : t`Campaign's banner has been updated`
    )

    navigateTo(linkBuilder.campaign(campaign.id))
  }

  const getImagePreviewUrl = (
    banner: CampaignBannerModelAsEditor
  ): string | null => {
    if (
      banner.image &&
      banner.image.imageVariants &&
      banner.image.imageVariants['1400x500']
    ) {
      return banner.image.imageVariants['1400x500'].cdnUrl
    } else if (banner.image && banner.image.cdnUrl) {
      return banner.image.cdnUrl
    } else {
      return null
    }
  }

  const { step } = state
  return (
    <>
      {step === CampaignActionEditingSteps.CREATE && (
        <StepCreate {...state} onStateUpdate={onStateUpdate} />
      )}
      {step === CampaignActionEditingSteps.PREVIEW && (
        <StepPreview {...state} onStateUpdate={onStateUpdate} />
      )}
      {step === CampaignActionEditingSteps.SCHEDULE && (
        <CampaignBannerStepSchedule
          {...state}
          onStateUpdate={onStateUpdate}
          onSave={onSave}
        />
      )}
    </>
  )
}

const StepCreate = (
  props: ICampaignBannerComponentState & {
    onStateUpdate: (
      updatedState: Partial<ICampaignBannerComponentState>
    ) => void
  }
) => {
  const bannerBodyId: string = uniqueId()
  const bannerHeaderId: string = uniqueId()
  const buttonLinkId: string = uniqueId()
  const buttonTextId: string = uniqueId()

  let inputFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (props.coverImageFile) {
      onCoverImage(props.coverImageFile)
    }
  }, [props.coverImageFile])

  const onCoverImage = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      props.onStateUpdate({ coverImageBase64: reader.result as string })
    }
    reader.readAsDataURL(file)
  }

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (files?.length) {
      const file = files[0]
      if (file.type.match('image.*')) {
        props.onStateUpdate({ coverImageFile: file, coverImageSrc: null })
      } else {
        props.onStateUpdate({
          coverImageFile: null,
          coverImageBase64: null,
          coverImageSrc: null
        })
      }
    }
  }

  const previewEnabled = (): boolean => {
    return !!(
      props.bannerHeader &&
      props.buttonLink &&
      props.buttonText &&
      (props.coverImageBase64 || props.coverImageSrc)
    )
  }

  const setStepPreview = () => {
    props.onStateUpdate({ step: CampaignActionEditingSteps.PREVIEW })
  }

  return (
    <div className="CampaignBannerComponent CampaignBannerComponent__create">
      <NewActionHeader
        title={t`Show a banner`}
        steps={CampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.CREATE}
        nextButton={
          <ButtonPrimary
            onClick={setStepPreview}
            disabled={!previewEnabled()}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Preview</Trans>
          </ButtonPrimary>
        }
      />

      <section>
        <TemplatingInfoBar />

        <Label htmlFor={bannerHeaderId}>
          <Trans>Banner header</Trans>
        </Label>
        <Input
          id={bannerHeaderId}
          fullWidth
          value={props.bannerHeader}
          onChange={(val) => {
            props.onStateUpdate({ bannerHeader: val })
          }}
        />
      </section>

      <section>
        <Label htmlFor={bannerBodyId}>
          <Trans>Banner body</Trans>{' '}
          <span className="optional">
            <Trans>optional</Trans>
          </span>
        </Label>
        <CustomQuill
          value={props.bannerBody}
          onChange={(content) => {
            if (!striptags(content.trim())) {
              content = ''
            }
            props.onStateUpdate({ bannerBody: content })
          }}
        />
      </section>

      <section>
        <Label htmlFor={bannerBodyId}>
          <Trans>Cover image</Trans>
        </Label>
        <div
          className={classNames('ImageUpload', {
            'has-preview': props.coverImageBase64 || props.coverImageSrc
          })}
        >
          {props.coverImageBase64 && (
            <img src={props.coverImageBase64} alt={t`cover image`} />
          )}
          {props.coverImageSrc && (
            <img src={props.coverImageSrc} alt={t`cover image`} />
          )}
          <input
            title={t`Cover image`}
            type="file"
            onChange={onImageChange}
            ref={inputFileRef}
            accept="image/*"
          />
          {((props.coverImageBase64 || props.coverImageSrc) && (
            <ButtonLight
              onClick={() => {
                if (inputFileRef.current) {
                  inputFileRef.current.click()
                }
              }}
            >
              <Trans>Change image</Trans>
            </ButtonLight>
          )) || (
            <ButtonLight
              onClick={() => {
                if (inputFileRef.current) {
                  inputFileRef.current.click()
                }
              }}
            >
              <Trans>Upload an image</Trans>
            </ButtonLight>
          )}
        </div>
      </section>

      <section>
        <Label htmlFor={buttonLinkId}>
          <Trans>Button link</Trans>
        </Label>
        <Input
          id={buttonLinkId}
          fullWidth
          value={props.buttonLink}
          type="url"
          pattern="^(ftp:\/\/|http:\/\/|https:\/\/).*$"
          onChange={(val) => {
            props.onStateUpdate({ buttonLink: val })
          }}
        />
      </section>

      <section>
        <Label htmlFor={buttonTextId}>
          <Trans>Button text</Trans>
        </Label>
        <Input
          id={buttonTextId}
          fullWidth
          value={props.buttonText}
          onChange={(val) => {
            props.onStateUpdate({ buttonText: val })
          }}
        />
      </section>

      <section className="CampaignBannerComponent__footer">
        <ButtonPrimary disabled={!previewEnabled()} onClick={setStepPreview}>
          <Trans>Preview message</Trans>
        </ButtonPrimary>
      </section>
    </div>
  )
}

const StepPreview = (
  props: ICampaignBannerComponentState & {
    onStateUpdate: (
      updatedState: Partial<ICampaignBannerComponentState>
    ) => void
  }
) => {
  // interpolation
  props = {
    ...props,
    bannerHeader: interpolate(props.bannerHeader, PREVIEW_VARS),
    buttonText: interpolate(props.buttonText, PREVIEW_VARS),
    bannerBody: interpolate(props.bannerBody, PREVIEW_VARS)
  }

  return (
    <div className="CampaignBannerComponent CampaignBannerComponent__preview">
      <NewActionHeader
        title={t`Show a banner`}
        steps={CampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.PREVIEW}
        prevButton={
          <ButtonLight
            onClick={() => {
              props.onStateUpdate({ step: CampaignActionEditingSteps.CREATE })
            }}
            leftIcon={<IconArrowLeft />}
          >
            <Trans>Back</Trans>
          </ButtonLight>
        }
        nextButton={
          <ButtonPrimary
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.SCHEDULE
              })
            }}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Schedule</Trans>
          </ButtonPrimary>
        }
      />

      <CampaignBanner
        buttonLink={props.buttonLink}
        buttonText={props.buttonText}
        text={props.bannerBody}
        header={props.bannerHeader}
        coverImageBase64={props.coverImageBase64}
        coverImageSrc={props.coverImageSrc}
        coverImageVariants={props.coverImageVariants}
        isPreview
      />
    </div>
  )
}
